import { AuditOutlined, BellOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileStatGrid, MobileStatItem, MobileWorkbenchCard } from '@/components/mobile/MobileWorkbenchCard';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { ROUTES } from '@/constants/routes';
import { useLeadershipTier } from '@/hooks/useLeadershipTier';
import { useLeaderTeamSnapshot } from '@/hooks/useLeaderTeamSnapshot';
import { useNotificationUnreadCount } from '@/hooks/useNotificationUnreadCount';
import * as departmentsApi from '@/services/api/departments';
import { buildDepartmentSubmissionStats } from '@/utils/leader-team';
import { flattenDepartmentTree } from '@/utils/department-tree';
import { getLocalDateKey } from '@/utils/date';

function TopLeaderHomeContent() {
  const today = dayjs();
  const { loading, subordinates, rows, stats, dateKey, reload } = useLeaderTeamSnapshot(today);
  const [departmentNameMap, setDepartmentNameMap] = useState<Map<string, string>>(new Map());
  const isToday = dateKey === getLocalDateKey();

  const loadDepartments = useCallback(async () => {
    try {
      const tree = await departmentsApi.fetchDepartmentTree();
      const map = new Map<string, string>();
      for (const dept of flattenDepartmentTree(tree)) {
        map.set(dept.id, dept.name);
      }
      setDepartmentNameMap(map);
    } catch {
      setDepartmentNameMap(new Map());
    }
  }, []);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  const departmentStats = useMemo(
    () => buildDepartmentSubmissionStats(rows, departmentNameMap),
    [departmentNameMap, rows],
  );

  return (
    <div className="mobile-workbench">
      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">日报管理</span>
        </div>
        <MobileWorkbenchCard
          accent
          title="监管日报"
          description="查看单位负责人日报"
          to={ROUTES.mobile.leader.leadership}
          icon={<AuditOutlined />}
        />
      </section>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">团队日报统计</span>
          <span className="mobile-section__meta">{isToday ? '今日' : dateKey}</span>
        </div>

        {loading ? (
          <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : subordinates.length === 0 ? (
          <MobileEmpty message="暂无直属下属，无法统计团队提交情况。" />
        ) : (
          <>
            <MobileStatGrid>
              <MobileStatItem label="已提交" value={stats.submitted} tone="success" />
              <MobileStatItem label="未提交" value={stats.notSubmitted} tone="warning" />
              <MobileStatItem label="提交率" value={stats.submitRate} suffix="%" />
            </MobileStatGrid>

            {departmentStats.length > 0 ? (
              <div className="mobile-dept-stats">
                <div className="mobile-dept-stats__title">各部门提交情况</div>
                {departmentStats.map((dept) => (
                  <div key={dept.departmentId} className="mobile-dept-stats__row">
                    <span className="mobile-dept-stats__name">{dept.departmentName}</span>
                    <span className="mobile-dept-stats__meta">
                      {dept.submitted}/{dept.total} · {dept.submitRate}%
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            <Link className="mobile-section-link" to={ROUTES.mobile.leader.team}>
              <TeamOutlined /> 查看团队日报详情
            </Link>
          </>
        )}
      </section>

      <button type="button" className="mobile-text-btn" onClick={() => void reload()}>
        刷新数据
      </button>
    </div>
  );
}

function DefaultLeaderHomeContent() {
  const today = dayjs();
  const { canViewLeadershipOversight } = useLeadershipTier();
  const { loading, subordinates, stats, dateKey, reload } = useLeaderTeamSnapshot(today);
  const { count: unreadCount, reload: reloadUnread } = useNotificationUnreadCount();
  const isToday = dateKey === getLocalDateKey();

  return (
    <div className="mobile-workbench">
      <MobileWorkbenchCard
        accent
        title="写日报"
        description="填写或提交今日工作日报"
        to={ROUTES.mobile.leader.myReport}
        icon={<EditOutlined />}
      />

      {canViewLeadershipOversight ? (
        <section className="mobile-section">
          <MobileWorkbenchCard
            title="监管日报"
            description="查看单位负责人已提交日报"
            to={ROUTES.mobile.leader.leadership}
            icon={<AuditOutlined />}
          />
        </section>
      ) : null}

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">我的消息</span>
          {unreadCount > 0 ? (
            <span className="mobile-section__meta mobile-section__meta--danger">{unreadCount} 条未读</span>
          ) : null}
        </div>
        <MobileWorkbenchCard
          title="消息中心"
          description={unreadCount > 0 ? `您有 ${unreadCount} 条未读消息` : '查看项目、任务与点评通知'}
          to={ROUTES.mobile.leader.notifications}
          icon={<BellOutlined />}
        />
      </section>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">团队日报统计</span>
          <span className="mobile-section__meta">{isToday ? '今日' : dateKey}</span>
        </div>

        {loading ? (
          <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : subordinates.length === 0 ? (
          <MobileEmpty message="暂无直属下属，无法统计团队提交情况。" />
        ) : (
          <>
            <MobileStatGrid>
              <MobileStatItem label="已提交" value={stats.submitted} tone="success" />
              <MobileStatItem label="未提交" value={stats.notSubmitted} tone="warning" />
              <MobileStatItem label="下属人数" value={stats.total} />
            </MobileStatGrid>

            <Link className="mobile-section-link" to={ROUTES.mobile.leader.team}>
              <TeamOutlined /> 查看团队日报详情
            </Link>
          </>
        )}
      </section>

      <button
        type="button"
        className="mobile-text-btn"
        onClick={() => {
          void reload();
          void reloadUnread();
        }}
      >
        刷新数据
      </button>
    </div>
  );
}

export function LeaderHomePage() {
  const { isTopLeader, loading: tierLoading } = useLeadershipTier();

  if (tierLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    );
  }

  return isTopLeader ? <TopLeaderHomeContent /> : <DefaultLeaderHomeContent />;
}
